import React, { useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import { Button } from '../../../shared/components/Button';
import { LAB_REPORT_TEMPLATES, LabReportTemplate } from '../../../templates/lab-report';
import { FlaskConical, PlusCircle, Check, Sparkles } from 'lucide-react';

interface LabTemplateSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: LabReportTemplate | null) => void;
}

export const LabTemplateSelectorModal: React.FC<LabTemplateSelectorModalProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(LAB_REPORT_TEMPLATES[0].id);

  const handleApply = () => {
    if (selectedTemplateId === 'blank') {
      onSelectTemplate(null);
    } else {
      const found = LAB_REPORT_TEMPLATES.find((t) => t.id === selectedTemplateId);
      onSelectTemplate(found || LAB_REPORT_TEMPLATES[0]);
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create / Load Laboratory Report Template"
      maxWidth="820px"
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Button
            variant="secondary"
            onClick={() => {
              onSelectTemplate(null);
              onClose();
            }}
          >
            Start Blank Canvas
          </Button>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleApply}>
              Load Selected Template
            </Button>
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)', margin: 0 }}>
          Choose a pre-configured lab report template matching East West University CSE/EEE lab curriculum (with formulas, datasheets, and sample answers), or start from a blank canvas.
        </p>

        {/* Choice Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          {LAB_REPORT_TEMPLATES.map((tpl) => {
            const isSelected = selectedTemplateId === tpl.id;
            return (
              <div
                key={tpl.id}
                onClick={() => setSelectedTemplateId(tpl.id)}
                style={{
                  border: `2px solid ${isSelected ? 'var(--primary-500)' : 'var(--border-subtle)'}`,
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-4)',
                  backgroundColor: isSelected ? 'var(--primary-50)' : 'var(--bg-surface)',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: 'var(--space-3)',
                  transition: 'all var(--transition-fast)',
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span className="badge badge-primary" style={{ fontSize: '10px' }}>
                      <Sparkles size={10} style={{ display: 'inline', marginRight: 2 }} />
                      {tpl.thumbnailBadge || 'EWU Lab'}
                    </span>
                    {isSelected && <Check size={16} color="var(--primary-600)" />}
                  </div>
                  <h4 style={{ margin: '0 0 6px 0', fontSize: 'var(--font-size-sm)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FlaskConical size={15} color="var(--primary-600)" />
                    {tpl.name}
                  </h4>
                  <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                    {tpl.description}
                  </p>
                </div>

                <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: '6px' }}>
                  <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-tertiary)' }}>
                    {tpl.sectionsBlueprint.length} Sections Blueprint:
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                    {tpl.sectionsBlueprint.map((sec, idx) => (
                      <span
                        key={idx}
                        style={{
                          fontSize: '9px',
                          padding: '1px 4px',
                          backgroundColor: 'var(--bg-surface-subtle)',
                          borderRadius: 'var(--radius-xs)',
                          border: '1px solid var(--border-subtle)',
                        }}
                      >
                        {sec.title}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Blank Option */}
          <div
            onClick={() => setSelectedTemplateId('blank')}
            style={{
              border: `2px solid ${selectedTemplateId === 'blank' ? 'var(--primary-500)' : 'var(--border-subtle)'}`,
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-4)',
              backgroundColor: selectedTemplateId === 'blank' ? 'var(--primary-50)' : 'var(--bg-surface)',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              transition: 'all var(--transition-fast)',
            }}
          >
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span className="badge badge-neutral" style={{ fontSize: '10px' }}>Empty Canvas</span>
                {selectedTemplateId === 'blank' && <Check size={16} color="var(--primary-600)" />}
              </div>
              <h4 style={{ margin: '0 0 6px 0', fontSize: 'var(--font-size-sm)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                <PlusCircle size={15} color="var(--text-secondary)" />
                Blank Lab Report
              </h4>
              <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                Start with an empty workspace and construct your own custom sections, mathematical formulas, and tables from scratch.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
